<template>
    <v-layout column>
        <expired-offers-snackbar :is-offers-expired="isOffersExpired" @refresh="refreshExpiredOffers" />
        <teleport v-if="$breakpoint.smAndDown" to="#under-header">
            <mobile-info-page-tabs v-model="mobileTabs" mobile-tab1-title="search" />
        </teleport>
        <v-container fluid>
            <tour-breadcrumbs v-if="!$breakpoint.smAndDown" :search-request="searchRequest" />
            <v-row>
                <v-col v-show="!$breakpoint.smAndDown || ($breakpoint.smAndDown && mobileTabs === 0)" md="8">
                    <v-card>
                        <v-card-text class="pa-4 pa-md-6 pa-lg-8">
                            <itinerary-info
                                :info="info"
                                :supplier-name="offer.info && offer.info.supplierName ? offer.info.supplierName : ''"
                            >
                                <template v-slot:bookingInfo>
                                    <v-card
                                        v-if="(totalPrice?.amount || searchActive) && !$breakpoint.smAndDown"
                                        variant="tonal"
                                        class="my-2 my-sm-6 pa-2 pa-sm-3"
                                    >
                                        <div class="float-right d-flex align-center justify-end">
                                            <div v-if="showPriceDuringSearch" class="text-h6 mr-4">
                                                <span class="text-medium-emphasis">{{ $t('total_price') }}:</span>
                                                {{ $filters.price(totalPrice) }}
                                            </div>
                                            <v-btn
                                                color="primary"
                                                :loading="showPriceDuringSearch && searchActive"
                                                :disabled="searchActive"
                                                style="z-index: 1"
                                                @click="action(selectForBooking)"
                                            >
                                                {{ label($t('book')) }}
                                            </v-btn>
                                        </div>
                                    </v-card>
                                </template>
                                <template v-slot:offerInfo>
                                    <template v-if="offer.info">
                                        <v-col cols="12" md="12" lg="4">
                                            <h6 class="text-h6 pb-1 pb-sm-3">{{ $t('summary') }}</h6>
                                            <tour-general-info :info="offer.info" :package-tour-info="info" />
                                        </v-col>
                                    </template>
                                    <v-skeleton-loader v-else-if="searchActive" class="full-width" type="paragraph" />
                                    <div
                                        v-else-if="!formattedDays.length"
                                        class="text-center text-subtitle-1 bg-red-lighten-3"
                                    >
                                        <span class="text-red-darken-3">{{ $t('tours.no_tours_message') }}</span>
                                    </div>
                                </template>
                            </itinerary-info>
                            <template v-if="sortedOffers?.length > 1">
                                <v-card class="activity-grouped-offers" elevation="0">
                                    <div>
                                        <v-card-title
                                            class="activity-grouped-offers__title text-body-1 align-baseline px-0 pb-6 cursor-pointer"
                                            @click="expanded = !expanded"
                                        >
                                            <span class="text-primary">{{ $t('available_offers') }}</span>
                                            <span class="ml-4 text-body-2 text-primary">
                                                <v-icon color="primary" size="small">
                                                    {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                                </v-icon>
                                            </span>
                                        </v-card-title>
                                        <v-expand-transition>
                                            <div v-if="expanded && sortedOffers">
                                                <TourOfferSubCard
                                                    v-for="offer in sortedOffers"
                                                    :key="offer.id"
                                                    :offer="offer"
                                                    :offer-data="offer.info.packageDate"
                                                    :offer-tariff="offer.info.offerInfo.tariffName"
                                                    :title="offer.info?.serviceName || offer.info?.name"
                                                    :secondary-service-name="offer.info?.secondaryServiceName"
                                                    :search-request="searchRequest"
                                                    :selected-offer="selectedOffer"
                                                    @select="selectOffer"
                                                />
                                            </div>
                                        </v-expand-transition>
                                    </div>
                                </v-card>
                            </template>
                            <v-btn
                                v-if="$breakpoint.smAndDown && (totalPrice?.amount || searchActive)"
                                block
                                color="primary"
                                :loading="showPriceDuringSearch && searchActive"
                                :disabled="searchActive"
                                @click="action(selectForBooking)"
                            >
                                {{ label($filters.price(totalPrice)) }}
                            </v-btn>
                        </v-card-text>
                    </v-card>
                    <v-row v-show="!$breakpoint.smAndDown || ($breakpoint.smAndDown && mobileTabs === 0)">
                        <v-col>
                            <h6 class="text-h4 pb-6 pt-10">{{ $t('details') }}</h6>
                            <template v-if="!searchActive">
                                <template v-for="(item, index) in formattedDays">
                                    <template
                                        v-if="
                                            item.dayOffers?.length ||
                                            item.dayOptions?.length ||
                                            item.mainDescription ||
                                            dayImage(item.day).url
                                        "
                                    >
                                        <v-card :key="'day-' + index" elevation="1" class="pa-4 mb-5">
                                            <v-row no-gutters>
                                                <v-col cols="12" md="1">
                                                    <div
                                                        class="text-medium-emphasis d-flex align-end align-md-center flex-md-column gc-2 width-max-content"
                                                    >
                                                        <div class="text-h5 text-capitalize">{{ $t('day', 1) }}</div>
                                                        <div class="text-h2 text-capitalize">{{ item.day }}</div>
                                                    </div>
                                                </v-col>
                                                <v-col cols="12" md="7" class="px-0 px-md-4 py-5 py-md-0">
                                                    <div
                                                        class="text-body-2"
                                                        :class="{'pl-4': /<ol>|<ul>/.test(item.mainDescription)}"
                                                        v-html="item.mainDescription"
                                                    />
                                                    <v-list
                                                        v-if="item?.dayOffers?.length && offer.info"
                                                        density="compact"
                                                    >
                                                        <template
                                                            v-for="dayOffer in item.dayOffers"
                                                            :key="dayOffer.offerKey"
                                                        >
                                                            <v-card
                                                                variant="outlined"
                                                                class="mt-3 py-4 px-5 full-width"
                                                            >
                                                                <tour-offer-entry
                                                                    class="tour-entity"
                                                                    :entry="dayOffer"
                                                                    :offer="offer"
                                                                    hide-date
                                                                    hide-entry-type
                                                                />
                                                            </v-card>
                                                        </template>
                                                    </v-list>
                                                    <v-skeleton-loader
                                                        v-else-if="searchActive && !offer.info"
                                                        transition="fade-transition"
                                                        type="article"
                                                    />
                                                    <v-list v-if="item.dayOptions?.length && offer.info">
                                                        <div class="text-h6 pt-2 pt-md-4">
                                                            {{ $t('tours.optional_services') }}
                                                        </div>
                                                        <template
                                                            v-for="dayOption in item.dayOptions"
                                                            :key="dayOption.offerKey"
                                                        >
                                                            <v-card variant="outlined" class="mt-3 pa-4 full-width">
                                                                <tour-offer-entry
                                                                    :entry="dayOption"
                                                                    :offer="offer"
                                                                    hide-date
                                                                    hide-entry-type
                                                                />
                                                            </v-card>
                                                        </template>
                                                    </v-list>
                                                </v-col>
                                                <v-col cols="12" md="4">
                                                    <v-img
                                                        :src="$filters.imageUrl(dayImage(item.day).url, 650)"
                                                        :lazy-src="$filters.srcHost('/placeholder.png')"
                                                        width="100%"
                                                    />
                                                </v-col>
                                            </v-row>
                                        </v-card>
                                    </template>
                                </template>
                            </template>
                            <v-skeleton-loader v-else transition="fade-transition" type="article" />
                        </v-col>
                    </v-row>
                </v-col>
                <v-col v-show="!$breakpoint.smAndDown || ($breakpoint.smAndDown && mobileTabs === 1)" md="4">
                    <tour-search-summary :search-request="searchRequest" :info="info" />
                    <div class="mb-5">
                        <tour-datepicker :search-request="searchRequest" />
                    </div>
                    <v-card class="pa-2">
                        <tour-map ref="map" />
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
        <conditions-modal />
        <tour-hotel-info-modal :search-request="searchRequest" />
        <tour-flight-info-modal />
        <tour-transfer-info-modal :search-request="searchRequest" />
        <tour-activities-info-modal :search-request="searchRequest" />
        <tour-extra-service-info-modal :search-request="searchRequest" />
        <tour-insurance-info-modal :search-request="searchRequest" />
        <tour-cruise-info-modal :search-request="searchRequest" />
        <tour-car-info-modal :search-request="searchRequest" />
        <hotel-room-info-modal />
        <hotel-meal-type-info-modal />
        <additional-fees-modal />
        <client-only>
            <v-dialog v-if="mapModal" v-model="mapModal" :max-width="500">
                <map-location :latitude="mapOffer.coordinates[0]" :longitude="mapOffer.coordinates[1]" height="500px" />
            </v-dialog>
        </client-only>
    </v-layout>
</template>

<script lang="ts">
    import {Component, mixins, Setup, Watch} from 'vue-facing-decorator'
    import {TourStoreMixin} from '@/mixins/TourStoreMixin.vue'
    import {TourPageMixin} from '@/components/tours/mixins/TourPageMixin.vue'
    import TourBreadcrumbs from '@/components/tours/TourBreadcrumbs.vue'
    import ConditionsModal from '@/components/modals/ConditionsModal.vue'
    import MobileInfoPageTabs from '@/components/snippets/MobileInfoPageTabs.vue'
    import {searchRequest} from '@/utils/tours/tours-blank-states'
    import TourSearchSummary from '@/components/tours/search/TourSearchSummary.vue'
    import {isEqual} from '@/utils/helpers'
    import TourGeneralInfo from '@/components/tours/snippets/TourGeneralInfo.vue'
    import TourMap from '@/components/tours/info/TourMap.vue'
    import TourDatepicker from '@/components/tours/search/forms/TourDatepicker.vue'
    import ExpiredOffersSnackbar from '@/components/search/ExpiredOffersSnackbar.vue'
    import ItineraryInfo from '@/components/info/ItineraryInfo.vue'
    import TourOfferEntry from '~src/components/tours/search/offers/tourOfferEntry.src'
    import TourHotelInfoModal from '@/components/tours/modals/TourHotelInfoModal.vue'
    import TourFlightInfoModal from '@/components/tours/modals/TourFlightInfoModal.vue'
    import TourTransferInfoModal from '@/components/tours/modals/TourTransferInfoModal.vue'
    import TourActivitiesInfoModal from '@/components/tours/modals/TourActivitiesInfoModal.vue'
    import TourExtraServiceInfoModal from '@/components/tours/modals/TourExtraServiceInfoModal.vue'
    import TourInsuranceInfoModal from '@/components/tours/modals/TourInsuranceInfoModal.vue'
    import {AUTH_EVENT, CHANGE_LOCALE_EVENT, EventBus, SHOW_ON_MAP_EVENT} from '@/utils/event-bus'
    import TourCruiseInfoModal from '@/components/tours/modals/TourCruiseInfoModal.vue'
    import TourCarInfoModal from '@/components/tours/modals/TourCarInfoModal.vue'
    import HotelMealTypeInfoModal from '@/components/hotels/modals/HotelMealTypeInfoModal.vue'
    import HotelRoomInfoModal from '@/components/modals/HotelRoomInfoModal.vue'
    import AdditionalFeesModal from '@/components/modals/AdditionalFeesModal.vue'
    import MapLocation from '~src/components/info/mapLocation.src'
    import {useTourRuntimeData} from '~/composables/tour'
    import TourOfferSubCard from '~/components/tours/search/offers/TourOfferSubCard.vue'
    import type {RouteLocationNormalized} from 'vue-router'

    @Component({
        components: {
            TourOfferSubCard,
            AdditionalFeesModal,
            HotelRoomInfoModal,
            HotelMealTypeInfoModal,
            TourCruiseInfoModal,
            ItineraryInfo,
            ExpiredOffersSnackbar,
            TourDatepicker,
            TourMap,
            TourGeneralInfo,
            TourSearchSummary,
            MobileInfoPageTabs,
            ConditionsModal,
            TourBreadcrumbs,
            TourOfferEntry,
            TourHotelInfoModal,
            TourFlightInfoModal,
            TourTransferInfoModal,
            TourActivitiesInfoModal,
            TourExtraServiceInfoModal,
            TourInsuranceInfoModal,
            TourCarInfoModal,
            MapLocation,
        },
        async setup() {
            const route = useRoute()
            const {packageTourId, arrivalCityId, departureCityId} = route.query
            await useAsyncData(`tour-${packageTourId}-${arrivalCityId}-${departureCityId}`, async () => {
                await useTourRuntimeData(route)
                return true
            })
        },
    })
    export class TourPage extends mixins(TourStoreMixin, TourPageMixin) {
        @Setup(() => useBookingPriceButton().action) action
        @Setup(() => useBookingPriceButton().label) label
        @Setup(() => useBookingPriceButton().showPriceDuringSearch) showPriceDuringSearch

        mobileTabs = 0
        options = {}
        expanded = true
        selectedOffer = null

        mapModal = false
        mapOffer = null

        @Watch('$route')
        onQueryChange(route: RouteLocationNormalized) {
            if (route.name !== 'tour') return
            this.loadOffers()
            if (this.$breakpoint.smAndDown) {
                this.mobileTabs = 0
            }
        }

        async fetch() {
            await this.loadRuntimeData()
        }

        async mounted() {
            EventBus.$on(AUTH_EVENT, this.refreshExpiredOffers)
            EventBus.$on(CHANGE_LOCALE_EVENT, this.refreshExpiredOffers)
            EventBus.$on(SHOW_ON_MAP_EVENT, this.showMap)
            this.$refs.map.initMarkers()
            await this.loadOffers()
        }

        beforeUnmount() {
            EventBus.$off(AUTH_EVENT, this.refreshExpiredOffers)
            EventBus.$off(CHANGE_LOCALE_EVENT, this.refreshExpiredOffers)
            EventBus.$off(SHOW_ON_MAP_EVENT, this.showMap)
        }

        async loadOffers() {
            await this.$store.restored
            const singleTourSearchRequest = {
                ...toursStore.singleTourSearchRequest,
                packageTourId: this.packageTourId,
                startDateFrom: toursStore.singleTourSearchRequest?.startDateFrom,
                startDateTo: toursStore.singleTourSearchRequest?.startDateFrom,
            }
            if (
                !isEqual(singleTourSearchRequest, this.searchRequest) ||
                this.isOffersExpired() ||
                !this.sortedOffers.length
            ) {
                await toursRuntimeStore.singleTourSearch(this.searchRequest)
            } else {
                const offers = await this.$localForage.getItem('tours')
                toursRuntimeStore.load(offers)
            }
        }

        async refreshExpiredOffers() {
            await toursRuntimeStore.singleTourSearch(this.searchRequest)
        }

        dayImage(dayNumber) {
            return this.info.images?.find(image => image.dayNumber === dayNumber) || {}
        }

        isOffersExpired() {
            return toursStore.isSingleHotelOffersExpired()
        }

        selectForBooking() {
            const offerKey = this.offer.offerKey
            toursStore.ADD_TO_BASKET(offerKey)
            this.$router.push({
                name: 'tourBooking',
                query: {offerKey},
            })
        }

        showMap(mapOffer) {
            this.mapOffer = mapOffer
            this.mapModal = true
        }

        get searchRequest() {
            //TODO Need filter $route.query params
            const rq = Object.assign(searchRequest(), this.$route.query)
            rq.convertToCurrency = persistentStore.currency
            rq.adults = parseInt(rq.adults, 10)
            if (typeof rq.childrenAges === 'string') {
                rq.childrenAges = [parseInt(rq.childrenAges, 10)]
            }
            rq.arrivalCityId = parseInt(rq.arrivalCityId, 10)
            if (rq.departureCityId) rq.departureCityId = parseInt(rq.departureCityId, 10)
            rq.durationFrom = parseInt(rq.durationFrom, 10)
            rq.durationTo = parseInt(rq.durationTo, 10)
            if (this.$route.query.startDateFrom) {
                rq.startDateFrom = this.$route.query.startDateFrom
                rq.startDateTo = this.$route.query.startDateFrom
            } else if (this.info.availabilities.length) {
                rq.startDateFrom = this.info.availabilities[0].startDateFrom
                rq.startDateTo = this.info.availabilities[0].startDateFrom
            }
            if (rq.citizenshipId) {
                rq.citizenshipId = parseInt(rq.citizenshipId, 10)
            }
            rq.packageTourId = parseInt(rq.packageTourId, 10)

            return rq
        }

        get packageTourId() {
            return parseInt(this.$route.query.packageTourId, 10)
        }

        get info() {
            return toursRuntimeStore.packageTourInfo
        }

        get offer() {
            if (this.selectedOffer) return this.selectedOffer
            return (
                this.sortedOffers?.[0] ||
                toursRuntimeStore.offers.find(offer => offer.info.id === this.packageTourId) ||
                {}
            )
        }

        selectOffer(offer) {
            this.selectedOffer = offer
        }

        get sortedOffers() {
            return toursStore.singleTourSearchResponse?.offers?.sort((a, b) => a.price.amount - b.price.amount) || []
        }

        get totalPrice() {
            return toursRuntimeStore.totalPrice(this.offer)
        }

        get searchActive() {
            return toursRuntimeStore.singleTourSearchActive
        }

        get formattedDays() {
            const days = {}
            let counter = 0
            let lastFlight = {}

            const productOffersAdapter = product =>
                product.offers?.length
                    ? product.offers
                    : product.packageOffers?.length
                      ? product.packageOffers
                      : [product]

            if (this.info.itineraryDays) {
                this.info.itineraryDays.forEach(item => {
                    days[item.number] = {
                        day: item.number,
                        mainDescription: item.description,
                        dayOffers: [],
                        dayOptions: [],
                    }
                })
                this.info.entries.forEach(entry => {
                    if (entry.dayNumber !== counter) counter = entry.dayNumber
                    let offerName, serviceName, selectedHotel, selectedOffer
                    const offerEntry = this.offer.entries?.find(el => el.entryName === entry.entryName)
                    if (offerEntry) {
                        switch (offerEntry.type) {
                            case 'ACCOMMODATION':
                                offerEntry.offers.forEach(hotel => {
                                    let room
                                    if (offerEntry.selectedOfferKey) {
                                        room = hotel.rooms.find(
                                            room =>
                                                room.groupedOffers.findIndex(
                                                    groupedOffer =>
                                                        groupedOffer.offerKey === offerEntry.selectedOfferKey
                                                ) !== -1
                                        )
                                    } else {
                                        room = hotel.rooms[0]
                                    }

                                    if (room) {
                                        serviceName = room.name
                                        selectedHotel = hotel

                                        return
                                    }
                                })
                                offerName = selectedHotel.name
                                break
                            case 'EXCURSION':
                            case 'EVENT':
                                offerName = offerEntry.name
                                for (const product of offerEntry.offers) {
                                    selectedOffer = productOffersAdapter(product).find(
                                        ({offerKey}) => offerKey === offerEntry.selectedOfferKey
                                    )
                                    if (selectedOffer) break
                                }
                                serviceName = selectedOffer ? selectedOffer.serviceName : ''
                                break
                            case 'EXTRA_SERVICE':
                                offerName = offerEntry.name
                                serviceName = offerEntry.selectedOfferKey
                                    ? offerEntry.offers.find(offer => offer.offerKey === offerEntry.selectedOfferKey)
                                          .info.service
                                    : offerEntry.offers[0].info.service
                                break
                            case 'FLIGHT':
                                if (offerEntry.offers[0].itinerary.length > 1 && offerEntry.obligatory) {
                                    offerName =
                                        offerEntry.offers[0].itinerary[0].segments[0].departure.airportDescription
                                            .airportName +
                                        ' - ' +
                                        offerEntry.offers[0].itinerary[0].segments[0].arrival.airportDescription
                                            .airportName

                                    lastFlight = Object.assign({}, offerEntry)
                                    lastFlight.offerName =
                                        lastFlight.offers[0].itinerary[1].segments[0].departure.airportDescription
                                            .airportName +
                                        ' - ' +
                                        lastFlight.offers[0].itinerary[1].segments[0].arrival.airportDescription
                                            .airportName
                                } else {
                                    offerName = offerEntry.name
                                }
                                break
                            case 'TRANSFER':
                                offerName = offerEntry.name
                                serviceName = offerEntry.selectedOfferKey
                                    ? offerEntry.offers.find(offer => offer.offerKey === offerEntry.selectedOfferKey)
                                          .info.description
                                    : offerEntry.offers[0].info.description
                                break
                            case 'INSURANCE':
                                offerName = offerEntry.name
                                serviceName = offerEntry.selectedOfferKey
                                    ? offerEntry.offers.find(offer => offer.offerKey === offerEntry.selectedOfferKey)
                                          .planName
                                    : offerEntry.offers[0].planName
                                break
                            case 'CRUISE':
                                offerName = offerEntry.name
                                for (const product of offerEntry.offers) {
                                    selectedOffer = productOffersAdapter(product).find(
                                        ({offerKey}) => offerKey === offerEntry.selectedOfferKey
                                    )
                                    if (selectedOffer) break
                                }
                                serviceName = selectedOffer
                                    ? `${selectedOffer.serviceName} / ${selectedOffer.className}`
                                    : ''
                                break
                        }
                        offerEntry.offerName = offerName
                        offerEntry.serviceName = serviceName
                        if (days[counter]) {
                            if (offerEntry.obligatory) days[counter]?.dayOffers.push(offerEntry)
                            else days[counter]?.dayOptions.push(offerEntry)
                        }
                    }
                })
            }
            if (Object.keys(lastFlight).length) days[this.info.itineraryDays.length]?.dayOffers.push(lastFlight)

            return Object.values(days)
        }
    }

    export default TourPage
</script>

<style scoped lang="scss">
    :deep(.lightbox .col) {
        background: rgba(0, 0, 0, 0.4);
    }
    .tour-entity {
        :deep(.v-list-item__prepend) {
            display: none;
        }
    }
    :deep(.v-list-item__prepend) {
        * {
            margin: 0 !important;
        }
    }
</style>
